import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import Contacts from "./pages/Contacts";
import Pipeline from "./pages/Pipeline";
import QuoteRequests from "./pages/QuoteRequests";
import Expenses from "./pages/Expenses";
import Vehicles from "./pages/Vehicles";
import AdminUsers from "./pages/AdminUsers";
import AdminFiles from "./pages/AdminFiles";
import Editorial from "./pages/Editorial";
import EditorialDetail from "./pages/EditorialDetail";
import Exports from "./pages/Exports";
import Commissions from "./pages/Commissions";
import CalendarPage from "./pages/CalendarPage";

// Components
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading, token } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!token || !user) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Layout>{children}</Layout>;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
    const { user, loading, token } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (token && user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />

            {/* Protected routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/companies"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <Companies />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/companies/:id"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <CompanyDetail />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/contacts"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <Contacts />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/pipeline"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <Pipeline />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/quote-requests"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <QuoteRequests />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/expenses"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <Expenses />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/vehicles"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <Vehicles />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/calendar"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'editorial', 'super_admin']}>
                        <CalendarPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/users"
                element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <AdminUsers />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/settings"
                element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <AdminUsers />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/editorial"
                element={
                    <ProtectedRoute allowedRoles={['editorial', 'assistant', 'super_admin']}>
                        <Editorial />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/editorial/:id"
                element={
                    <ProtectedRoute allowedRoles={['editorial', 'assistant', 'super_admin']}>
                        <EditorialDetail />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/exports"
                element={
                    <ProtectedRoute allowedRoles={['assistant', 'super_admin']}>
                        <Exports />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/commissions"
                element={
                    <ProtectedRoute allowedRoles={['commercial', 'assistant', 'super_admin']}>
                        <Commissions />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/files"
                element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <AdminFiles />
                    </ProtectedRoute>
                }
            />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster position="top-right" richColors closeButton />
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
